import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
    Box, 
    Button, 
    Card, 
    CardContent, 
    Typography, 
    Stepper, 
    Step, 
    StepLabel,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Grid,
    Autocomplete,
    Switch,
    FormControlLabel
} from '@mui/material';
import { 
    Upload, 
    FileText, 
    Table as TableIcon, 
    Settings, 
    CheckCircle,
    Download,
    Save,
    Trash2,
    Edit
} from 'lucide-react';
import { useApiData, MainTableItem } from '../../contexts/apiService';
import * as XLSX from 'xlsx';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// Fuzzy matching function to find best column match
const fuzzyMatchColumn = (label: string, availableColumns: string[]): string | null => {
    if (!label || availableColumns.length === 0) return null;
    
    const normalizedLabel = label.toLowerCase().trim()
        .replace(/[^a-z0-9\s]/g, '') // Remove special chars
        .replace(/\s+/g, '_'); // Replace spaces with underscores
    
    // Try exact match first
    const exactMatch = availableColumns.find(col => 
        col.toLowerCase() === normalizedLabel
    );
    if (exactMatch) return exactMatch;
    
    // Try partial match
    const partialMatch = availableColumns.find(col => 
        col.toLowerCase().includes(normalizedLabel) || 
        normalizedLabel.includes(col.toLowerCase())
    );
    if (partialMatch) return partialMatch;
    
    // Calculate similarity scores for all columns
    const scores = availableColumns.map(col => {
        const normalizedCol = col.toLowerCase();
        let score = 0;
        
        // Word-based matching
        const labelWords = normalizedLabel.split(/[\s_]+/);
        const colWords = normalizedCol.split(/[\s_]+/);
        
        labelWords.forEach(labelWord => {
            colWords.forEach(colWord => {
                if (colWord.includes(labelWord) || labelWord.includes(colWord)) {
                    score += 3;
                } else if (colWord.startsWith(labelWord) || labelWord.startsWith(colWord)) {
                    score += 2;
                }
            });
        });
        
        // Character-based similarity (Levenshtein-inspired)
        const shorter = normalizedLabel.length < normalizedCol.length ? normalizedLabel : normalizedCol;
        const longer = normalizedLabel.length < normalizedCol.length ? normalizedCol : normalizedLabel;
        
        for (let i = 0; i < shorter.length; i++) {
            if (shorter[i] === longer[i]) score += 0.5;
        }
        
        return { column: col, score };
    });
    
    // Sort by score and return best match if score is good enough
    scores.sort((a, b) => b.score - a.score);
    
    if (scores[0].score > 2) {
        return scores[0].column;
    }
    
    return null;
};

interface ExtractedData {
    columns: string[];
    data: string[][];
    totalPages?: number;
    pageNumber?: number;
}

interface MainTableData {
    columns: string[];
}

interface CellSelection {
    row: number;
    col: number;
    value: string;
}

interface CellMapping {
    label: CellSelection;
    value: CellSelection;
    destinationColumn: string;
}

interface MappingConfig {
    id: number;
    carrier_name: string;
    mapping_name: string;
    pdf_columns: string[];
    excel_columns: string[];
    column_mapping: Record<string, string>;
    notes?: string;
}

const Sandbox: React.FC = () => {
    const { 
        extractTableFromPdf, 
        saveColumnMapping, 
        getCarrierMappings,
        checkDuplicateTicket,
        apiData,
        handleAddRow,
        userDetails,
        pdfUploaded
    } = useApiData();

    // State management
    const [activeStep, setActiveStep] = useState(0);
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
    const [selectedCarrier, setSelectedCarrier] = useState<string>('');
    const [mainTableColumns, setMainTableColumns] = useState<string[]>([]);
    
    // Cell-level mapping state
    const [cellMappings, setCellMappings] = useState<CellMapping[]>([]);
    const [currentLabelCell, setCurrentLabelCell] = useState<CellSelection | null>(null);
    const [currentValueCell, setCurrentValueCell] = useState<CellSelection | null>(null);
    const [mappingMode, setMappingMode] = useState<'label' | 'value'>('label');
    
    const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
    const [mappedData, setMappedData] = useState<Record<string, string>[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    
    // PDF upload toggle
    const [enablePdfUpload, setEnablePdfUpload] = useState(true);
    
    // Duplicate detection - default to true (assume duplicate until proven otherwise)
    const [hasDuplicate, setHasDuplicate] = useState(true);
    
    // Dialog states
    const [saveMappingOpen, setSaveMappingOpen] = useState(false);
    const [carrierName, setCarrierName] = useState('');
    const [mappingName, setMappingName] = useState('');
    const [mappingNotes, setMappingNotes] = useState('');
    
    // Saved mappings
    const [savedMappings, setSavedMappings] = useState<MappingConfig[]>([]);
    const [selectedMapping, setSelectedMapping] = useState<number | null>(null);

    const steps = ['Upload PDF', 'Select Carrier', 'Map Columns', 'Review & Save'];

    // Get unique carriers from carrier_definition
    const uniqueCarriers = useMemo(() => {
        if (apiData?.carrier_definition) {
            return apiData.carrier_definition
                .map((carrier: any) => carrier.name)
                .filter((name: string) => name) // Remove empty values
                .sort();
        }
        return [];
    }, [apiData?.carrier_definition]);

    // Get main_table column names dynamically from MainTableItem interface
    // Uses actual data if available, otherwise falls back to typed constant
    const mainTableColumnsList = useMemo(() => {
        // If we have main_table data, extract columns from first item
        if (apiData?.main_table && apiData.main_table.length > 0) {
            return Object.keys(apiData.main_table[0]) as (keyof MainTableItem)[];
        }
        
        // Fallback: Define columns based on MainTableItem interface
        // This list matches the MainTableItem interface in apiService.tsx
        const columns: (keyof MainTableItem)[] = [
            'id', 'rid', 'ticket_number', 'producer', 'lease', 'carrier',
            'load_date', 'load_time', 'requestDate', 'responseTime',
            'gross_barrels', 'net_barrels', 'observed_gravity', 'observed_temperature',
            'corrected_gravity', 'bsw', 'driver', 'station_owner', 'destination', 'market',
            'offload_lane', 'second_offload_destination', 'second_offload_market', 'second_offload_lane',
            'second_offload_gross', 'second_offload_net', 'third_offload_destination', 'third_offload_market',
            'third_offload_lane', 'third_offload_gross', 'third_offload_net',
            'unload_start_date', 'unload_start_time', 'producer_whitelist', 'lease_whitelist',
            'destination_whitelist', 'mileage', 'rate', 'approved_mileage', 'approved_rate',
            'status', 'month', 'year', 'month_year_mo', 'delivered_my', 'delivered_my_mo',
            'trucking', 'purchase_deduct', 'purchase_bsw_deduct', 'purchase_price',
            'additional_deduct', 'sale_deduct', 'sale_bsw_deduct', 'additional_charges',
            'location_deduct', 'sale_location_deduct', 'sale_price',
            'total_purchase', 'total_sale', 'station_charge', 'net_profit', 'trucking_net'
        ];
        return columns;
    }, [apiData?.main_table]);

    // Set main table columns on mount
    useEffect(() => {
        setMainTableColumns(mainTableColumnsList);
    }, [mainTableColumnsList]);

    // Get available columns (excluding already mapped ones)
    const availableColumns = useMemo(() => {
        const mappedColumns = new Set(cellMappings.map(m => m.destinationColumn));
        return mainTableColumns.filter(col => !mappedColumns.has(col));
    }, [mainTableColumns, cellMappings]);

    // PDF Upload Handler
    const onPdfDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setPdfFile(file);
            setError(null);
            setSuccess(`PDF uploaded: ${file.name}`);
        }
    }, []);

    const { getRootProps: getPdfRootProps, getInputProps: getPdfInputProps, isDragActive: isPdfDragActive } = useDropzone({
        onDrop: onPdfDrop,
        accept: { 'application/pdf': ['.pdf'] },
        multiple: false
    });

    // Extract table from PDF
    const handleExtractTable = async () => {
        if (!pdfFile) {
            setError('Please upload a PDF file first');
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const result = await extractTableFromPdf(pdfFile, 0);
            if (result?.success) {
                setExtractedData({
                    columns: result.columns,
                    data: result.data,
                    totalPages: result.total_pages,
                    pageNumber: result.page_number
                });
                // Auto-select carrier by fuzzy matching extracted fields
                let foundCarrier = '';
                const allFields = [...result.columns, ...result.data.flat()];
                for (const field of allFields) {
                    if (!field) continue;
                    const normField = field.toLowerCase().replace(/[^a-z0-9]/g, '');
                    for (const carrier of uniqueCarriers) {
                        const normCarrier = carrier.toLowerCase().replace(/[^a-z0-9]/g, '');
                        if (normCarrier && normField && (normCarrier === normField || normCarrier.includes(normField) || normField.includes(normCarrier))) {
                            foundCarrier = carrier;
                            break;
                        }
                    }
                    if (foundCarrier) break;
                }
                if (foundCarrier) {
                    setSelectedCarrier(foundCarrier);
                    setCarrierName(foundCarrier);
                    handleLoadMappings(foundCarrier);
                    setSuccess(`Table extracted and carrier auto-selected: ${foundCarrier}`);
                } else {
                    setSuccess('Table extracted successfully!');
                }
                setActiveStep(1);
            } else {
                setError(result?.message || 'Failed to extract table from PDF');
            }
        } catch (err) {
            setError('Error extracting table from PDF');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Load saved mappings for a carrier
    const handleLoadMappings = async (carrier: string) => {
        setLoading(true);
        try {
            const result = await getCarrierMappings(carrier);
            if (result?.success) {
                setSavedMappings(result.mappings || []);
            }
        } catch (err) {
            console.error('Error loading mappings:', err);
        } finally {
            setLoading(false);
        }
    };

    // Apply a saved mapping
    const handleApplySavedMapping = (mapping: MappingConfig) => {
        const mappingData = mapping.column_mapping as any;
        
        // Check if this is a cell-based mapping
        if (mappingData.type === 'cell_based' && mappingData.mappings) {
            // Convert saved cell mappings back to CellMapping format
            const restoredMappings: CellMapping[] = mappingData.mappings.map((m: any) => ({
                label: { row: m.label.row, col: m.label.col, value: m.label.value },
                value: { row: m.value.row, col: m.value.col, value: m.value.value },
                destinationColumn: m.destinationColumn
            }));
            
            setCellMappings(restoredMappings);
            setCarrierName(mapping.carrier_name);
            setMappingName(mapping.mapping_name);
            setSuccess(`Applied cell-based mapping: ${mapping.mapping_name} with ${restoredMappings.length} field mappings`);
            
            // Auto-apply the mapping to create output data
            const mappedRow: Record<string, string> = {};
            restoredMappings.forEach(m => {
                // Try to find the value in the current extracted data using the same coordinates
                if (extractedData) {
                    if (m.value.row === -1) {
                        // Header row - get from columns array
                        if (extractedData.columns[m.value.col]) {
                            mappedRow[m.destinationColumn] = extractedData.columns[m.value.col];
                        } else {
                            mappedRow[m.destinationColumn] = m.value.value;
                        }
                    } else if (extractedData.data[m.value.row] && extractedData.data[m.value.row][m.value.col]) {
                        // Data row
                        mappedRow[m.destinationColumn] = extractedData.data[m.value.row][m.value.col];
                    } else {
                        // If coordinates don't match, use the saved value as fallback
                        mappedRow[m.destinationColumn] = m.value.value;
                    }
                } else {
                    // No extracted data available, use saved value
                    mappedRow[m.destinationColumn] = m.value.value;
                }
            });
            
            setMappedData([mappedRow]);
            
            // Check for duplicate ticket number after applying saved mapping
            const ticketNumber = mappedRow['ticket_number'];
            if (ticketNumber) {
                setLoading(true);
                setHasDuplicate(true); // Assume duplicate until proven otherwise
                checkDuplicateTicket(ticketNumber)
                    .then(existingTicket => {
                        if (existingTicket) {
                            setHasDuplicate(true);
                            setError(`⚠️ Duplicate Warning: Ticket ${ticketNumber} already exists in the database. Submit button disabled.`);
                        } else {
                            setHasDuplicate(false);
                        }
                    })
                    .catch(err => {
                        console.error('Error checking for duplicate in saved mapping:', err);
                        setHasDuplicate(true); // Keep disabled if check fails (safer approach)
                        setError('Saved mapping applied! Warning: Unable to verify duplicate status - please check manually.');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                setHasDuplicate(true); // No ticket number means we can't verify
                setError('Saved mapping applied! Warning: No ticket_number found - cannot check for duplicates.');
            }
            
            setActiveStep(3); // Move to review step
        } else {
            // Legacy column-based mapping
            setColumnMapping(mapping.column_mapping);
            setCarrierName(mapping.carrier_name);
            setMappingName(mapping.mapping_name);
            setSuccess(`Applied column mapping: ${mapping.mapping_name}`);
        }
    };

    // Update column mapping
    const handleMappingChange = (pdfColumn: string, excelColumn: string) => {
        setColumnMapping(prev => ({
            ...prev,
            [pdfColumn]: excelColumn
        }));
    };

    // Save mapping configuration
    const handleSaveMapping = async () => {
        if (!selectedCarrier || !mappingName) {
            setError('Please fill in carrier name and mapping name');
            return;
        }

        if (cellMappings.length === 0) {
            setError('No cell mappings to save');
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            // Convert cell mappings to a format that can be saved and reapplied
            const cellMappingData = {
                type: 'cell_based',
                mappings: cellMappings.map(m => ({
                    label: { row: m.label.row, col: m.label.col, value: m.label.value },
                    value: { row: m.value.row, col: m.value.col, value: m.value.value },
                    destinationColumn: m.destinationColumn
                }))
            };

            const result = await saveColumnMapping(
                selectedCarrier,
                mappingName,
                [], // No simple PDF columns for cell-based mappings
                mainTableColumns,
                cellMappingData as any, // Send cell mapping data (typed as any to avoid TypeScript error with complex nested structure)
                mappingNotes
            );
            
            if (result?.success) {
                setSuccess(`Mapping "${mappingName}" saved successfully for ${selectedCarrier}!`);
                setSaveMappingOpen(false);
                setMappingName('');
                setMappingNotes('');
                // Reload mappings to include the newly saved one
                await handleLoadMappings(selectedCarrier);
            } else {
                setError(result?.message || 'Failed to save mapping');
            }
        } catch (err) {
            setError('Error saving mapping');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Export mapped data to Excel
    const handleExport = () => {
        if (!mappedData) {
            setError('No data to export');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(mappedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Mapped Data');
        XLSX.writeFile(workbook, `mapped_data_${Date.now()}.xlsx`);
        setSuccess('Data exported successfully!');
    };

    // Handle cell click for mapping
    const handleCellClick = (row: number, col: number, value: string) => {
        const cellSelection: CellSelection = { row, col, value };
        
        if (mappingMode === 'label') {
            setCurrentLabelCell(cellSelection);
            setMappingMode('value');
            
            // Try to find best matching column using fuzzy match
            const suggestedColumn = fuzzyMatchColumn(value, availableColumns);
            if (suggestedColumn) {
                setColumnMapping({ ...columnMapping, temp: suggestedColumn });
                setSuccess(`Label selected: "${value}". Suggested column: "${suggestedColumn}". Now click the value cell.`);
            } else {
                setSuccess(`Label selected: "${value}". Now click the cell with the value.`);
            }
        } else {
            setCurrentValueCell(cellSelection);
            
            // If no column was suggested, try to match based on value as well
            if (!columnMapping.temp) {
                const suggestedColumn = fuzzyMatchColumn(currentLabelCell?.value || value, availableColumns);
                if (suggestedColumn) {
                    setColumnMapping({ ...columnMapping, temp: suggestedColumn });
                    setSuccess(`Value selected: "${value}". Suggested column: "${suggestedColumn}". Click "Add Mapping" or change the column.`);
                } else {
                    setSuccess(`Value selected: "${value}". Choose a destination column and click "Add Mapping".`);
                }
            } else {
                setSuccess(`Value selected: "${value}". Click "Add Mapping" to save or change the suggested column.`);
            }
        }
    };

    // Add a cell mapping
    const handleAddMapping = () => {
        if (!currentLabelCell || !currentValueCell || !columnMapping.temp) {
            setError('Please complete the mapping selection');
            return;
        }

        const newMapping: CellMapping = {
            label: currentLabelCell,
            value: currentValueCell,
            destinationColumn: columnMapping.temp
        };

        setCellMappings(prev => [...prev, newMapping]);
        
        // Reset for next mapping
        setCurrentLabelCell(null);
        setCurrentValueCell(null);
        setMappingMode('label');
        setColumnMapping(prev => {
            const { temp, ...rest } = prev;
            return rest;
        });
        setSuccess(`Mapping added: ${newMapping.label.value} → ${newMapping.destinationColumn}`);
    };

    // Delete a cell mapping
    const handleDeleteMapping = (index: number) => {
        setCellMappings(prev => prev.filter((_, idx) => idx !== index));
        setSuccess('Mapping deleted');
    };

    // Apply cell mappings and create output data
    const handleApplyCellMapping = async () => {
        if (cellMappings.length === 0) {
            setError('Please create at least one mapping');
            return;
        }

        // Create a single row of mapped data based on the cell mappings
        const mappedRow: Record<string, string> = {};
        
        cellMappings.forEach(mapping => {
            mappedRow[mapping.destinationColumn] = mapping.value.value;
        });

        setMappedData([mappedRow]);
        
        // Check for duplicate ticket number immediately after mapping
        const ticketNumber = mappedRow['ticket_number'];
        if (ticketNumber) {
            setLoading(true);
            setHasDuplicate(true); // Assume duplicate until proven otherwise
            try {
                const existingTicket = await checkDuplicateTicket(ticketNumber);
                
                if (existingTicket) {
                    setHasDuplicate(true);
                    setError(`⚠️ Duplicate Warning: Ticket ${ticketNumber} already exists in the database. Submit button disabled.`);
                    setSuccess(''); // Clear success message when showing duplicate warning
                } else {
                    setHasDuplicate(false);
                    setSuccess('Cell mappings applied successfully! No duplicate found.');
                }
            } catch (err) {
                console.error('Error checking for duplicate:', err);
                setHasDuplicate(true); // Keep disabled if check fails (safer approach)
                setError('Cell mappings applied! Warning: Unable to verify duplicate status - please check manually.');
            } finally {
                setLoading(false);
            }
        } else {
            setHasDuplicate(true); // No ticket number means we can't verify
            setError('Cell mappings applied! Warning: No ticket_number found - cannot check for duplicates.');
        }
        
        setActiveStep(3);
    };

    // Re-check for duplicates manually
    const handleRecheckDuplicate = async () => {
        if (!mappedData || mappedData.length === 0) {
            setError('No mapped data to check');
            return;
        }

        const ticketNumber = mappedData[0]['ticket_number'];
        if (!ticketNumber) {
            setError('No ticket number to check');
            setHasDuplicate(true);
            return;
        }

        setLoading(true);
        setHasDuplicate(true); // Assume duplicate until proven otherwise
        try {
            console.log('Re-checking for duplicate ticket:', ticketNumber);
            const existingTicket = await checkDuplicateTicket(ticketNumber);
            
            if (existingTicket) {
                setHasDuplicate(true);
                setError(`⚠️ Duplicate Warning: Ticket ${ticketNumber} already exists in the database. Submit button disabled.`);
            } else {
                setHasDuplicate(false);
                setSuccess(`✓ No duplicate found! Ticket ${ticketNumber} is unique. You may submit.`);
                setError(null);
            }
        } catch (err) {
            console.error('Error re-checking for duplicate:', err);
            setHasDuplicate(true);
            setError('Error checking for duplicate - please verify manually before submitting.');
        } finally {
            setLoading(false);
        }
    };

    // Edit cell in mapped data
    const handleCellEdit = (rowIndex: number, column: string, newValue: string) => {
        if (!mappedData) return;
        
        const updatedData = [...mappedData];
        updatedData[rowIndex] = { ...updatedData[rowIndex], [column]: newValue };
        setMappedData(updatedData);
        setSuccess(`Updated ${column} to "${newValue}"`);
        
        // If ticket_number was edited, we need to re-verify (set back to true until proven false)
        if (column === 'ticket_number') {
            setHasDuplicate(true);
            setError('Ticket number changed - click "Re-check Duplicate" to verify before submitting.');
        }
    };

    // Submit mapped data to database
    const handleSubmitToDatabase = async () => {
        if (!mappedData || mappedData.length === 0) {
            setError('No data to submit');
            return;
        }

        if (enablePdfUpload && !pdfFile) {
            setError('No PDF file available for upload');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Filter to only include non-empty required fields
            const cleanedData = mappedData[0];
            
            // Remove empty fields to use database defaults
            const filteredData = Object.fromEntries(
                Object.entries(cleanedData).filter(([_, value]) => value !== '' && value !== null)
            );

            // Ensure minimum required fields are present
            const requiredFields = ['carrier', 'ticket_number'];
            const missingFields = requiredFields.filter(field => !filteredData[field] || filteredData[field] === '');
            
            if (missingFields.length > 0) {
                setError(`Missing required fields: ${missingFields.join(', ')}`);
                return;
            }

            // Check for existing ticket with same ticket_number in entire database
            const ticketNumber = filteredData.ticket_number;
            const existingTicket = await checkDuplicateTicket(ticketNumber);

            if (existingTicket) {
                const userConfirmed = window.confirm(
                    `Warning: A ticket with number "${ticketNumber}" already exists in the database.\n\n` +
                    `Existing ticket details:\n` +
                    `- Carrier: ${existingTicket.carrier || 'N/A'}\n` +
                    `- Producer: ${existingTicket.producer || 'N/A'}\n` +
                    `- Load Date: ${existingTicket.load_date || 'N/A'}\n` +
                    `- Gross Barrels: ${existingTicket.gross_barrels || 'N/A'}\n\n` +
                    `Do you want to add this as a duplicate entry?`
                );

                if (!userConfirmed) {
                    setLoading(false);
                    setError('Submission cancelled - duplicate ticket number detected');
                    return;
                }
            }
            filteredData['new_row'] = '1'; // Indicate new row addition for backend processing
            // Submit to main_table
            await handleAddRow('main_table', filteredData, 1);
            
            let successMessage = `Successfully added ticket ${filteredData.ticket_number || 'unknown'} from ${filteredData.carrier || 'unknown'} to database`;
            
            // Conditionally upload PDF
            if (enablePdfUpload && pdfFile) {
                const ticketNumber = filteredData.ticket_number || 'unknown';
                const carrier = filteredData.carrier || 'unknown';
                const producer = filteredData.producer || 'unknown';
                
                // Create new filename: {ticket_number}_{carrier}_{producer}.pdf
                const newFileName = `${ticketNumber}_${carrier}_${producer}.pdf`;
                
                // Create a new File object with the new name
                const renamedFile = new File([pdfFile], newFileName, { type: pdfFile.type });
                
                // Upload to Firebase Storage
                const storage = getStorage();
                const storageRef = ref(storage, `unassigned_tickets/${newFileName}`);
                const uploadTask = uploadBytesResumable(storageRef, renamedFile);
                
                // Wait for upload to complete
                await new Promise<void>((resolve, reject) => {
                    uploadTask.on(
                        'state_changed',
                        (snapshot) => {
                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setSuccess(`Uploading PDF: ${Math.round(progress)}%`);
                        },
                        (error) => {
                            console.error('PDF upload error:', error);
                            reject(error);
                        },
                        async () => {
                            await getDownloadURL(uploadTask.snapshot.ref);
                            resolve();
                        }
                    );
                });
                
                // Notify system that PDF was uploaded
                pdfUploaded();
                
                successMessage += ' and uploaded PDF!';
            } else {
                successMessage += '!';
            }
            
            setSuccess(successMessage);
            
            // Wait a moment then reset for next entry
            setTimeout(() => {
                setActiveStep(0);
                setPdfFile(null);
                setExtractedData(null);
                setColumnMapping({});
                setCellMappings([]);
                setMappedData(null);
                setHasDuplicate(true); // Reset to assume duplicate for next entry
                // Keep carrier selected for convenience
            }, 2000);
            
        } catch (err) {
            setError('Error submitting to database or uploading PDF. Please check the data and try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                PDF Table Extraction Tool
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Extract tabular data from carrier PDF reports and map to your main_table structure
            </Typography>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ my: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Alerts */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {/* Step 0: Upload PDF */}
            {activeStep === 0 && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    <FileText size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                    Upload PDF Report
                                </Typography>
                                <Box
                                    {...getPdfRootProps()}
                                    sx={{
                                        border: '2px dashed #ccc',
                                        borderRadius: 2,
                                        p: 4,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        bgcolor: isPdfDragActive ? 'action.hover' : 'background.paper',
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <input {...getPdfInputProps()} />
                                    <Upload size={48} style={{ color: '#999' }} />
                                    <Typography variant="body1" sx={{ mt: 2 }}>
                                        {pdfFile ? pdfFile.name : 'Drag & drop PDF file here, or click to select'}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setPdfFile(null);
                                    setExtractedData(null);
                                    setSelectedCarrier('');
                                    setColumnMapping({});
                                    setMappedData(null);
                                }}
                            >
                                Reset
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleExtractTable}
                                disabled={!pdfFile || loading}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Extract Table'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            )}

            {/* Step 1: Select Carrier & View Extracted Data */}
            {activeStep === 1 && extractedData && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Select Carrier & View Extracted Data
                        </Typography>
                        
                        {/* Carrier Selection */}
                        <Box sx={{ mb: 3 }}>
                            <Autocomplete
                                options={uniqueCarriers}
                                value={selectedCarrier}
                                onChange={(event, newValue) => {
                                    setSelectedCarrier(newValue || '');
                                    if (newValue) {
                                        setCarrierName(newValue);
                                        handleLoadMappings(newValue);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Select Carrier" 
                                        placeholder="Type to search carriers..."
                                        required
                                    />
                                )}
                                fullWidth
                            />
                            {selectedCarrier && savedMappings.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Found {savedMappings.length} saved mapping(s) for {selectedCarrier}. Select one to auto-apply:
                                    </Alert>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Apply Saved Mapping</InputLabel>
                                        <Select
                                            value={selectedMapping || ''}
                                            onChange={(e) => {
                                                const mapping = savedMappings.find(m => m.id === e.target.value);
                                                if (mapping) {
                                                    handleApplySavedMapping(mapping);
                                                    setSelectedMapping(e.target.value as number);
                                                }
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Don't apply - create new mapping</em>
                                            </MenuItem>
                                            {savedMappings.map((mapping) => (
                                                <MenuItem key={mapping.id} value={mapping.id}>
                                                    {mapping.mapping_name} {mapping.notes && `- ${mapping.notes}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </Box>

                        {/* Extracted Data Preview */}
                        <Typography variant="subtitle1" gutterBottom>
                            Extracted PDF Data ({extractedData.data.length} rows, {extractedData.columns.length} columns)
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {extractedData.columns.map((col, idx) => (
                                            <TableCell key={idx}><strong>{col}</strong></TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {extractedData.data.slice(0, 50).map((row, rowIdx) => (
                                        <TableRow key={rowIdx}>
                                            {row.map((cell, cellIdx) => (
                                                <TableCell key={cellIdx}>{cell}</TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {extractedData.data.length > 50 && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                                Showing first 50 rows of {extractedData.data.length} total rows
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button onClick={() => setActiveStep(0)}>Back</Button>
                            <Button 
                                variant="contained" 
                                onClick={() => setActiveStep(2)}
                                disabled={!selectedCarrier}
                            >
                                Continue to Mapping
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Step 2: Column Mapping */}
            {activeStep === 2 && extractedData && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            <Settings size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                            Map PDF Data to Main Table Fields
                        </Typography>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>How to create mappings:</strong><br/>
                            1. Click a cell containing the field label/name<br/>
                            2. Click the cell containing the value for that field<br/>
                            3. Select the destination column in main_table<br/>
                            4. Click "Add Mapping" to save this mapping
                        </Alert>

                        {/* Current Mapping Instructions */}
                        <Paper sx={{ p: 2, mb: 2, bgcolor: mappingMode === 'label' ? 'primary.light' : 'success.light' }}>
                            <Typography variant="body1">
                                {mappingMode === 'label' ? (
                                    <>
                                        <strong>Step 1:</strong> Click the cell containing the field LABEL
                                        {currentLabelCell && ` (Selected: ${currentLabelCell.value})`}
                                    </>
                                ) : (
                                    <>
                                        <strong>Step 2:</strong> Click the cell containing the field VALUE
                                        {currentValueCell && ` (Selected: ${currentValueCell.value})`}
                                    </>
                                )}
                            </Typography>
                        </Paper>

                        {/* PDF Data Grid */}
                        <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2, border: '2px solid #1976d2' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: 'grey.200' }}><strong>Row/Col</strong></TableCell>
                                        {extractedData.columns.map((col, colIdx) => (
                                            <TableCell key={colIdx} sx={{ bgcolor: 'grey.200', textAlign: 'center' }}>
                                                <strong>{colIdx + 1}</strong>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* Clickable Column Headers Row */}
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: 'grey.100' }}><strong>0</strong></TableCell>
                                        {extractedData.columns.map((col, colIdx) => {
                                            const isLabelSelected = currentLabelCell?.row === -1 && currentLabelCell?.col === colIdx;
                                            const isValueSelected = currentValueCell?.row === -1 && currentValueCell?.col === colIdx;
                                            const isInMapping = cellMappings.some(m => 
                                                (m.label.row === -1 && m.label.col === colIdx) ||
                                                (m.value.row === -1 && m.value.col === colIdx)
                                            );
                                            
                                            return (
                                                <TableCell 
                                                    key={colIdx}
                                                    onClick={() => handleCellClick(-1, colIdx, col)}
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                        bgcolor: isLabelSelected ? 'primary.main' : 
                                                                isValueSelected ? 'success.main' :
                                                                isInMapping ? 'grey.300' : 'white',
                                                        color: (isLabelSelected || isValueSelected) ? 'white' : 'inherit',
                                                        '&:hover': {
                                                            bgcolor: isLabelSelected ? 'primary.dark' : 
                                                                    isValueSelected ? 'success.dark' :
                                                                    'grey.200'
                                                        }
                                                    }}
                                                >
                                                    {col}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                    
                                    {/* Data Rows */}
                                    {extractedData.data.map((row, rowIdx) => (
                                        <TableRow key={rowIdx}>
                                            <TableCell sx={{ bgcolor: 'grey.100' }}><strong>{rowIdx + 1}</strong></TableCell>
                                            {row.map((cell, colIdx) => {
                                                const isLabelSelected = currentLabelCell?.row === rowIdx && currentLabelCell?.col === colIdx;
                                                const isValueSelected = currentValueCell?.row === rowIdx && currentValueCell?.col === colIdx;
                                                const isInMapping = cellMappings.some(m => 
                                                    (m.label.row === rowIdx && m.label.col === colIdx) ||
                                                    (m.value.row === rowIdx && m.value.col === colIdx)
                                                );
                                                
                                                return (
                                                    <TableCell 
                                                        key={colIdx}
                                                        onClick={() => handleCellClick(rowIdx, colIdx, cell)}
                                                        sx={{ 
                                                            cursor: 'pointer',
                                                            bgcolor: isLabelSelected ? 'primary.main' : 
                                                                    isValueSelected ? 'success.main' :
                                                                    isInMapping ? 'grey.300' : 'white',
                                                            color: (isLabelSelected || isValueSelected) ? 'white' : 'inherit',
                                                            '&:hover': {
                                                                bgcolor: isLabelSelected ? 'primary.dark' : 
                                                                        isValueSelected ? 'success.dark' :
                                                                        'grey.200'
                                                            }
                                                        }}
                                                    >
                                                        {cell}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Destination Column Selection */}
                        {currentLabelCell && currentValueCell && (
                            <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Creating mapping: <Chip label={currentLabelCell.value} size="small" color="primary" /> 
                                    {' → '} 
                                    <Chip label={currentValueCell.value} size="small" color="success" />
                                </Typography>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={8}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Select Main Table Column</InputLabel>
                                            <Select
                                                value={columnMapping.temp || ''}
                                                onChange={(e) => setColumnMapping({ ...columnMapping, temp: e.target.value })}
                                            >
                                                {availableColumns.length === 0 ? (
                                                    <MenuItem disabled>
                                                        <em>All columns mapped</em>
                                                    </MenuItem>
                                                ) : (
                                                    availableColumns.map((col, idx) => (
                                                        <MenuItem key={idx} value={col}>{col}</MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Button 
                                            variant="contained" 
                                            fullWidth
                                            onClick={handleAddMapping}
                                            disabled={!columnMapping.temp}
                                        >
                                            Add Mapping
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}

                        {/* Current Mappings List */}
                        {cellMappings.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom><strong>Current Mappings:</strong></Typography>
                                <TableContainer component={Paper}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Label Cell</strong></TableCell>
                                                <TableCell><strong>Value Cell</strong></TableCell>
                                                <TableCell><strong>Destination</strong></TableCell>
                                                <TableCell><strong>Actions</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {cellMappings.map((mapping, idx) => {
                                                const labelLocation = mapping.label.row === -1 
                                                    ? `Header-C${mapping.label.col + 1}` 
                                                    : `R${mapping.label.row + 1}C${mapping.label.col + 1}`;
                                                const valueLocation = mapping.value.row === -1 
                                                    ? `Header-C${mapping.value.col + 1}` 
                                                    : `R${mapping.value.row + 1}C${mapping.value.col + 1}`;
                                                
                                                return (
                                                    <TableRow key={idx}>
                                                        <TableCell>
                                                            <Chip 
                                                                label={`${labelLocation}: ${mapping.label.value}`} 
                                                                size="small" 
                                                                color="primary" 
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                label={`${valueLocation}: ${mapping.value.value}`} 
                                                                size="small" 
                                                                color="success" 
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <strong>{mapping.destinationColumn}</strong>
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleDeleteMapping(idx)}
                                                                color="error"
                                                            >
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button onClick={() => setActiveStep(1)}>Back</Button>
                            <Button 
                                variant="contained" 
                                onClick={handleApplyCellMapping}
                                disabled={cellMappings.length === 0 || loading}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Apply Mappings & Continue'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Step 3: Review Mapped Data */}
            {activeStep === 3 && mappedData && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            <CheckCircle size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                            Review Mapped Data ({mappedData.length} rows)
                        </Typography>

                        {/* Summary Stats */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
                                    <Typography variant="h4">{mappedData.length}</Typography>
                                    <Typography variant="body2">Total Rows</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                                    <Typography variant="h4">{Object.keys(mappedData[0] || {}).length}</Typography>
                                    <Typography variant="body2">Mapped Fields</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                                    <Typography variant="h4">{selectedCarrier}</Typography>
                                    <Typography variant="body2">Carrier</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                                    <Typography variant="h4">{extractedData?.columns.length || 0}</Typography>
                                    <Typography variant="body2">PDF Columns</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Mapped Fields Display */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Mapped Fields:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {Object.keys(mappedData[0] || {}).map((col, idx) => (
                                    <Chip key={idx} label={col} color="success" size="small" />
                                ))}
                            </Box>
                        </Box>

                        {/* Data Table */}
                        <TableContainer component={Paper} sx={{ maxHeight: 400, mb: 2 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>#</strong></TableCell>
                                        {Object.keys(mappedData[0] || {}).map((col, idx) => (
                                            <TableCell key={idx}><strong>{col}</strong></TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mappedData.slice(0, 100).map((row, rowIdx) => (
                                        <TableRow key={rowIdx} hover>
                                            <TableCell>{rowIdx + 1}</TableCell>
                                            {Object.entries(row).map(([key, value], cellIdx) => (
                                                <TableCell 
                                                    key={cellIdx}
                                                    onDoubleClick={() => {
                                                        const newValue = prompt(`Edit ${key}:`, value);
                                                        if (newValue !== null) handleCellEdit(rowIdx, key, newValue);
                                                    }}
                                                    sx={{ cursor: 'pointer' }}
                                                    title="Double-click to edit"
                                                >
                                                    {value}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {mappedData.length > 100 && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                Showing first 100 rows of {mappedData.length} total rows
                            </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                            💡 Tip: Double-click any cell to edit its value before submitting
                        </Typography>

                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <strong>Review Required:</strong> Please verify all data is correct before submitting to the database. 
                            Required fields: carrier, ticket_number
                        </Alert>

                        {/* PDF Upload Toggle */}
                        <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.light' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={enablePdfUpload}
                                        onChange={(e) => setEnablePdfUpload(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                            Upload PDF to Firebase Storage
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {enablePdfUpload 
                                                ? `PDF will be uploaded as: ${mappedData[0]?.ticket_number || 'unknown'}_${selectedCarrier}_${mappedData[0]?.producer || 'unknown'}.pdf`
                                                : 'Data will be submitted without PDF upload'
                                            }
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Paper>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button onClick={() => setActiveStep(2)}>Back to Mapping</Button>
                            <Box>
                                <Button 
                                    onClick={handleRecheckDuplicate}
                                    variant="outlined"
                                    color={hasDuplicate ? 'error' : 'success'}
                                    sx={{ mr: 1 }}
                                    disabled={loading || !mappedData || !mappedData[0]?.ticket_number}
                                >
                                    {hasDuplicate ? '⚠️ Re-check Duplicate' : '✓ Verified Unique'}
                                </Button>
                                <Button 
                                    onClick={() => setSaveMappingOpen(true)}
                                    startIcon={<Save size={18} />}
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                    disabled={cellMappings.length === 0}
                                >
                                    Save Mapping for {selectedCarrier}
                                </Button>
                                <Button 
                                    onClick={handleExport}
                                    startIcon={<Download size={18} />}
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                >
                                    Export to Excel
                                </Button>
                                <Button 
                                    onClick={handleSubmitToDatabase}
                                    startIcon={<CheckCircle size={18} />}
                                    variant="contained"
                                    color="success"
                                    disabled={loading || hasDuplicate}
                                    sx={{ mr: 1 }}
                                    title={hasDuplicate ? 'Cannot submit duplicate ticket' : ''}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Submit to Database'}
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => {
                                        setActiveStep(0);
                                        setPdfFile(null);
                                        setExtractedData(null);
                                        setSelectedCarrier('');
                                        setColumnMapping({});
                                        setCellMappings([]);
                                        setMappedData(null);
                                        setHasDuplicate(false);
                                    }}
                                >
                                    Start New Extraction
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Save Mapping Dialog */}
            <Dialog open={saveMappingOpen} onClose={() => setSaveMappingOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Save Column Mapping for {selectedCarrier}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                        This mapping will be saved for carrier: <strong>{selectedCarrier}</strong>
                    </Alert>
                    <TextField
                        label="Mapping Name"
                        value={mappingName}
                        onChange={(e) => setMappingName(e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                        placeholder="e.g., Standard Daily Report"
                        helperText="Give this mapping a descriptive name for future reference"
                    />
                    <TextField
                        label="Notes (Optional)"
                        value={mappingNotes}
                        onChange={(e) => setMappingNotes(e.target.value)}
                        fullWidth
                        margin="normal"
                        multiline
                        rows={3}
                        placeholder="e.g., Used for daily production reports with volume data"
                    />
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                            Mapped Fields: {Object.keys(columnMapping).filter(k => columnMapping[k]).length} / {extractedData?.columns.length || 0}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveMappingOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleSaveMapping} 
                        variant="contained"
                        disabled={!selectedCarrier || !mappingName || loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Save Mapping'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Sandbox;